// Marketing-editable copy for Solar Surge: run-summary tiers, headlines, and the
// real-world impact framing. Kept out of the engine so wording can change without
// touching the simulation. Claims here passed the messaging & positioning check:
// energy is framed as "value of the electricity," never a guaranteed bill cut, and
// no tier implies net-metering sell-back (which is not universal).

export type Tier = {
  /** flavor line shown under the tier label */
  copy: string;
  label: string;
  /** lowest kWh that earns this tier */
  min: number;
};

/** The five Solar Surge tiers, ordered by ascending kWh threshold. */
export const TIERS: Tier[] = [
  { copy: 'Enough to charge your phone a few times.', label: 'Rookie', min: 0 },
  { copy: 'Enough to keep your lights on for a full day.', label: 'Amp Cadet', min: 15 },
  { copy: 'Enough to run a fridge for about a week.', label: 'Watt Scout', min: 35 },
  { copy: 'A month of evening lighting, covered.', label: 'Grid Hero', min: 75 },
  { copy: "Enough to run your home's essentials for days.", label: 'Solar Legend', min: 150 },
];

/** Score to tier label, flavor copy, and 1-based index (1..5). */
export const milestoneTier = (kwh: number): { copy: string; index: number; label: string } => {
  const i = TIERS.reduce((best, tier, idx) => (kwh >= tier.min ? idx : best), 0);
  return { copy: TIERS[i].copy, index: i + 1, label: TIERS[i].label };
};

export const headlineFor = (isHighScore: boolean): string => (isHighScore ? 'New best run!' : 'A cloud got you');

/** Real-world equivalence labels for the end card. */
export const IMPACT_LABELS = {
  co2: 'CO₂ kept out of the air',
  home: "of an average home's power",
  money: 'value of that electricity',
  phone: 'phone charges',
} as const;

export const IMPACT_FOOTNOTE = 'Figures are illustrative, based on US national averages:';

/** Reputable public sources for the equivalence factors shown on the run-summary card. */
export const IMPACT_SOURCES = [
  { href: 'https://www.epa.gov/egrid', label: 'EPA eGRID', note: 'about 0.39 kg CO₂ per kWh' },
  { href: 'https://www.eia.gov/electricity/', label: 'EIA', note: 'about $0.16 per kWh' },
] as const;

/** Homeowner-facing title-case for level names ("PARTLY CLOUDY" to "Partly Cloudy"). */
export const titleCase = (value: string): string => value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
